import { renderShieldArtwork } from './ShieldArtwork';
import { renderCrossbowArtwork } from './CrossbowArtwork';
import { renderClassWeaponArtwork } from './ClassWeaponArtwork';

import type { WeaponConfig } from '../AppearanceDefinitions';
import type { EquipmentVisualResolution, ItemEquipmentVisualDescriptor } from '../../../shared/equipmentVisuals';
import { poseTypeForFamily, isSupportedEquipmentVisualFamily } from '../../../shared/equipmentVisuals';
import { attachmentAnchorsForPose } from './AttachmentGeometry';
import type { AttachmentAnchor, HumanoidPose } from './AttachmentGeometry';

export type WeaponPose = HumanoidPose;

/**
 * Families that map directly to an existing WeaponType render path.
 * These reuse WeaponParts.render() with the mapped type.
 * Families not listed here have dedicated code-native geometry below.
 */
const FAMILIES_VIA_WEAPON_TYPE: Record<string, WeaponConfig['type']> = {
  sword: 'sword',
  wand: 'wand',
  bow: 'bow',
  staff: 'staff',
  dagger: 'dagger',
  axe: 'axe',
  mace: 'mace',
};

interface LocalPoint {
  x: number;
  y: number;
}

function roundCoordinate(value: number): number {
  return Number(value.toFixed(2));
}

/**
 * Convert a body-rig anchor into the weapon's local coordinate space.
 *
 * Two-handed equipment must follow both authored hands. Keeping this inverse
 * transform beside the forward weapon transform prevents a second grip from
 * becoming another classic-body-only magic number.
 */
function localPointForBodyAnchor(
  anchor: AttachmentAnchor,
  primaryHand: AttachmentAnchor,
  angleDegrees: number,
  primaryGrip: LocalPoint,
): LocalPoint {
  const radians = angleDegrees * Math.PI / 180;
  const dx = anchor.x - primaryHand.x;
  const dy = anchor.y - primaryHand.y;
  return {
    x: roundCoordinate(dx * Math.cos(radians) + dy * Math.sin(radians) + primaryGrip.x),
    y: roundCoordinate(-dx * Math.sin(radians) + dy * Math.cos(radians) + primaryGrip.y),
  };
}

export class WeaponParts {

  static render(
    weapon: WeaponConfig,
    pose: WeaponPose = 'idle',
    handAnchor: AttachmentAnchor = attachmentAnchorsForPose(pose, weapon.type).frontHand,
    secondaryHandAnchor?: AttachmentAnchor,
  ): string {
    const { type, color, accentColor, variant } = weapon;

    // Helpers
    const createRect = (x: number, y: number, w: number, h: number, fill: string, rx = 0, transform = '') => {
      const tr = transform ? ` transform="${transform}"` : '';
      return `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${fill}" rx="${rx}"${tr} />`;
    };

    const createCircle = (cx: number, cy: number, r: number, fill: string, stroke = 'none', strokeWidth = 0) =>
      `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" />`;

    const angleByPose: Record<WeaponPose, number> = {
      idle: 12,
      blink: 12,
      walkA: 18,
      walkB: 5,
      attack: 48,
      cast: 15,
      hurt: -10,
      jump: 22,
      fall: -2,
      death: 78,
    };
    // A drawn bow aims along its own +X axis. Keep it upright during attacks
    // so the nocked arrow points unmistakably toward the authored right-facing
    // side; runtime centered mirroring carries that exact axis to the left.
    const attackAngleByType: Record<WeaponConfig['type'], number> = {
      sword: 48,
      staff: 28,
      axe: 42,
      bow: 0,
      dagger: 50,
      mace: 45,
      wand: 42,
    };
    const gripByType: Record<WeaponConfig['type'], { x: number; y: number }> = {
      sword: { x: 5, y: 11 },
      staff: { x: 5, y: 8 },
      axe: { x: 5, y: 9 },
      bow: { x: 0, y: 0 },
      dagger: { x: 5.5, y: 10 },
      mace: { x: 5, y: 9 },
      wand: { x: 5, y: 9 },
    };
    const angle = pose === 'attack' ? attackAngleByType[type] : angleByPose[pose];
    const grip = pose === 'cast' && type === 'staff'
      ? { x: 5, y: -6 }
      : pose === 'cast' && type === 'wand'
        ? { x: 5, y: -3 }
        : gripByType[type];
    const secondaryGrip = secondaryHandAnchor
      ? localPointForBodyAnchor(secondaryHandAnchor, handAnchor, angle, grip)
      : { x: -19, y: 0 };
    const transform = `translate(${handAnchor.x} ${handAnchor.y}) rotate(${angle}) translate(${-grip.x} ${-grip.y})`;

    // D42: the Weapon Training class weapons draw their own art.
    let content = renderClassWeaponArtwork(type, variant ?? 0, color, accentColor) ?? '';

    if (!content) switch (type) {
    case 'sword':
      content = `
            <path d="M0,0 L0,-40 L5,-45 L10,-40 L10,0 Z" fill="${color}" stroke="#555" stroke-width="1" />
            ${createRect(-5, 0, 20, 5, accentColor)}
            ${createRect(2, 5, 6, 15, '#8B4513')}
        `;
      if (variant === 2) { // Katana / Scimitar
        content = `
                <path d="M2,5 Q-5,-20 8,-45 L12,-40 Q5,-20 8,5 Z" fill="${color}" stroke="#555" stroke-width="1" />
                ${createRect(0, 5, 12, 4, accentColor)}
                ${createRect(3, 9, 6, 15, '#333')}
            `;
      }
      if (variant === 3) { // Broadsword
        content = `
                <path d="M-2,0 L-5,-35 L5,-45 L15,-35 L12,0 Z" fill="${color}" stroke="#444" stroke-width="1" />
                ${createRect(-8, 0, 26, 6, accentColor)}
                ${createCircle(5, 3, 3, accentColor)}
                ${createRect(2, 6, 6, 12, '#5d4037')}
            `;
      }
      break;

    case 'staff':
      content = `
            ${createRect(2, -40, 6, 60, '#8B4513')}
            ${createCircle(5, -45, 8, color, accentColor, 2)}
        `;
      if (variant === 2) { // Twisted Staff
        content = `
                <path d="M5,20 Q10,0 5,-20 Q0,-40 5,-60" fill="none" stroke="#8B4513" stroke-width="5" />
                ${createCircle(5, -60, 6, color, accentColor, 2)}
                 <path d="M5,-60 L5,-70 M5,-50 L5,-40 M-5,-60 L15,-60" stroke="${accentColor}" stroke-width="1" />
            `;
      }
      break;

    case 'axe':
      content = `
             ${createRect(2, -30, 6, 50, '#8B4513')}
            <path d="M8,-25 Q25,-35 25,-10 Q8,0 8,-10 Z" fill="${color}" stroke="#555" stroke-width="1" />
            <path d="M2,-25 Q-15,-35 -15,-10 Q2,0 2,-10 Z" fill="${color}" stroke="#555" stroke-width="1" />
        `;
      break;

    case 'bow':
      if (pose === 'attack') {
        const arrowTailX = secondaryGrip.x - 3;
        content = `
            <path d="M0,-30 Q20,0 0,30" fill="none" stroke="${color}" stroke-width="3" />
            <path d="M0,-30 L${secondaryGrip.x},${secondaryGrip.y} L0,30" fill="none" stroke="#eee" stroke-width="1.2" />
            ${createRect(-2, -5, 4, 10, accentColor)}
            <g data-aim-axis="right">
            <path d="M${arrowTailX},${secondaryGrip.y} L34,0" stroke="#d7d2c4" stroke-width="1.8" stroke-linecap="round" />
            <path d="M34,0 L27,-4 L28,0 L27,4 Z" fill="${accentColor}" />
            <path d="M${secondaryGrip.x},${secondaryGrip.y} L${secondaryGrip.x - 6},${secondaryGrip.y - 4} M${secondaryGrip.x},${secondaryGrip.y} L${secondaryGrip.x - 6},${secondaryGrip.y + 4}" stroke="${accentColor}" stroke-width="1.5" />
            </g>
            <circle data-weapon-secondary-grip="back-hand" cx="${secondaryGrip.x}" cy="${secondaryGrip.y}" r="0" />`;
      } else {
        content = `
            <path d="M0,-30 Q20,0 0,30" fill="none" stroke="${color}" stroke-width="3" />
            <path d="M0,-30 L0,30" fill="none" stroke="#eee" stroke-width="1" />
            ${createRect(-2, -5, 4, 10, accentColor)}
          `;
      }
      break;

    case 'dagger':
      content = `
            <path d="M1,2 L1,-24 L6,-32 L10,-23 L9,2 Z" fill="${color}" stroke="#454b55" stroke-width="1" />
            ${createRect(-3, 1, 16, 4, accentColor, 1)}
            ${createRect(3, 5, 5, 12, '#33251f', 2)}
        `;
      break;

    case 'mace':
      content = `
            ${createRect(2, -24, 6, 44, '#684a2e', 2)}
            <path d="M-5,-28 L2,-37 L10,-37 L17,-28 L13,-17 L-1,-17 Z" fill="${color}" stroke="#4b5058" stroke-width="1.5" />
            ${createCircle(6, -27, 4, accentColor)}
        `;
      break;

    case 'wand':
      content = `
            ${createRect(3, -27, 4, 47, '#5c4331', 2)}
            <path d="M5,-39 L8,-31 L17,-31 L10,-25 L13,-16 L5,-22 L-3,-16 L0,-25 L-7,-31 L2,-31 Z" fill="${accentColor}" stroke="${color}" stroke-width="1.2" />
        `;
      break;
    }

    return `<g data-weapon-attachment="front-hand" data-anchor-x="${handAnchor.x}" data-anchor-y="${handAnchor.y}" data-grip-x="${grip.x}" data-grip-y="${grip.y}" transform="${transform}">
      ${content}
      <circle data-weapon-grip="front-hand" cx="${grip.x}" cy="${grip.y}" r="0" />
    </g>`;
  }

  /**
   * Render a weapon from a resolved equipment visual resolution (S2).
   *
   * Only `resolved` resolutions produce geometry. The descriptor's family
   * maps to a render path via the canonical registry. Families with a direct
   * WeaponType mapping reuse WeaponParts.render(). Families without (crossbow,
   * spear, shield, tome, glaive, fishing_pole, totem) use dedicated
   * code-native geometry. Unknown/unsupported families fail closed (no
   * geometry). Colors come from the descriptor, not a global weapon config.
   * Semantic data attributes are included.
   *
   * The hand anchor is derived from the resolved family's pose mapping,
   * not hard-coded sword geometry.
   */
  static renderResolved(
    resolution: EquipmentVisualResolution,
    pose: WeaponPose = 'idle',
    handAnchor?: AttachmentAnchor,
    secondaryHandAnchor?: AttachmentAnchor,
  ): string {
    if (resolution.kind !== 'resolved') return '';
    const d = resolution.descriptor;
    if (!isSupportedEquipmentVisualFamily('mainHand', d.family)) return '';

    // Derive the pose type from the family for anchor computation.
    const poseType = poseTypeForFamily(d.family);
    if (!poseType) return '';

    // Derive hand anchor from the resolved family's pose mapping.
    const anchor = handAnchor ?? attachmentAnchorsForPose(pose, poseType).frontHand;

    // Check if this family maps to an existing WeaponType render path.
    const weaponType = FAMILIES_VIA_WEAPON_TYPE[d.family];
    if (weaponType) {
      const weapon: WeaponConfig = {
        type: weaponType,
        color: d.primaryColor,
        accentColor: d.accentColor ?? d.primaryColor,
        variant: d.variant,
      };
      const inner = WeaponParts.render(weapon, pose, anchor, secondaryHandAnchor);
      return inner.replace(
        '<g data-weapon-attachment',
        `<g data-equipment-slot="${d.schemaSlot}" data-equipment-item-id="${d.itemId}" data-equipment-family="${d.family}" data-equipment-variant="${d.variant}" data-weapon-attachment`,
      );
    }

    const angleByPose: Record<WeaponPose, number> = {
      idle: 12, blink: 12, walkA: 18, walkB: 5, attack: 48,
      cast: 15, hurt: -10, jump: 22, fall: -2, death: 78,
    };
    const angle = d.family === 'crossbow' && pose === 'attack' ? 0 : angleByPose[pose];
    const grip = { x: 5, y: 10 };
    const secondaryGrip = d.family === 'crossbow' && pose === 'attack'
      ? localPointForBodyAnchor(
        secondaryHandAnchor ?? attachmentAnchorsForPose(pose, poseType).backHand,
        anchor,
        angle,
        grip,
      )
      : undefined;
    const content = renderNativeWeaponFamily(d, pose, secondaryGrip);
    if (!content) return '';
    const transform = `translate(${anchor.x} ${anchor.y}) rotate(${angle}) translate(${-grip.x} ${-grip.y})`;

    return `<g data-equipment-slot="${d.schemaSlot}" data-equipment-item-id="${d.itemId}" data-equipment-family="${d.family}" data-equipment-variant="${d.variant}" data-weapon-attachment="front-hand" data-anchor-x="${anchor.x}" data-anchor-y="${anchor.y}" data-grip-x="${grip.x}" data-grip-y="${grip.y}" transform="${transform}">
      ${content}
      <circle data-weapon-grip="front-hand" cx="${grip.x}" cy="${grip.y}" r="0" />
    </g>`;
  }
}

/**
 * Code-native geometry for weapon families without a corresponding WeaponType.
 * Each function returns SVG content in local weapon space (grip at ~5,10).
 * Returns '' for unknown families (fail closed).
 */
function renderNativeWeaponFamily(
  d: ItemEquipmentVisualDescriptor,
  pose: WeaponPose,
  secondaryGrip?: LocalPoint,
): string {
  const primary = d.primaryColor;
  const accent = d.accentColor ?? d.primaryColor;

  switch (d.family) {
  case 'crossbow':
    return renderCrossbowArtwork(primary, accent, { pose, secondaryGrip });

  case 'spear':
    return '<rect x="4" y="-35" width="3" height="50" fill="#5d4037" />'
        + `<path d="M2,-35 L5,-45 L8,-35 Z" fill="${primary}" stroke="${accent}" stroke-width="1" />`
        + `<rect x="2" y="5" width="6" height="4" fill="${accent}" />`;

  case 'shield':
    return renderShieldArtwork(primary, accent, d.variant);

  case 'tome':
    return `<rect x="-2" y="-12" width="14" height="18" rx="1" fill="${primary}" stroke="${accent}" stroke-width="1.5" />`
        + `<rect x="0" y="-10" width="10" height="14" fill="${accent}" opacity="0.3" />`
        + `<path d="M-2,-8 L-5,-8 L-5,2 L-2,2" fill="none" stroke="${accent}" stroke-width="1" />`
        + '<rect x="2" y="6" width="6" height="4" fill="#5d4037" />';

  case 'glaive':
    return '<rect x="4" y="-20" width="3" height="35" fill="#5d4037" />'
        + `<path d="M7,-20 Q20,-25 18,-5 Q12,-10 7,-12 Z" fill="${primary}" stroke="${accent}" stroke-width="1" />`
        + `<rect x="2" y="10" width="6" height="4" fill="${accent}" />`;

  case 'fishing_pole':
    return '<path d="M5,10 Q3,-10 8,-35" fill="none" stroke="#5d4037" stroke-width="2.5" stroke-linecap="round" />'
        + `<path d="M8,-35 Q15,-30 12,-20" fill="none" stroke="${primary}" stroke-width="1" />`
        + `<circle cx="12" cy="-20" r="2" fill="${accent}" />`
        + `<rect x="3" y="6" width="5" height="8" fill="${accent}" rx="1" />`;

  case 'totem':
    return renderClassWeaponArtwork('totem', d.variant, primary, accent)
      ?? `<rect x="0" y="-20" width="10" height="30" rx="2" fill="${primary}" stroke="${accent}" stroke-width="1.5" />`
        + `<circle cx="5" cy="-15" r="3" fill="${accent}" />`
        + `<path d="M2,-5 L8,-5 M2,0 L8,0 M2,5 L8,5" stroke="${accent}" stroke-width="1" />`
        + '<rect x="2" y="10" width="6" height="5" fill="#5d4037" />';

  default:
    return '';
  }
}
